interface Entry {
  [key: string]: number;
}

interface RoundingData {
  roundedPercent: number;
  truePercent: number;
}

export const convertDataCountToPercentages = <T extends Entry>(initialValues: T): T => {
  const result: Entry = {};
  const dataMap = new Map<string, RoundingData>();
  let total = 0;
  let totalPercentageAfterRounding = 0;

  // Early return if all values are 0. There will be nothing to compute.
  if (Object.keys(initialValues).every((key) => initialValues[key] === 0)) {
    return initialValues;
  }

  Object.keys(initialValues).forEach((key: string) => {
    total = total + initialValues[key];
  });

  Object.keys(initialValues).forEach((key: string) => {
    const truePercent = (initialValues[key] / total) * 100;
    const roundedPercent = Number(truePercent.toFixed(1));

    dataMap.set(key, {
      truePercent,
      roundedPercent,
    });

    totalPercentageAfterRounding = totalPercentageAfterRounding + roundedPercent;
  });

  if (totalPercentageAfterRounding === 100) {
    dataMap.forEach((value, key) => {
      result[key] = value.roundedPercent;
    });
    return result as T;
  }

  return handleRoundingError<T>(dataMap, totalPercentageAfterRounding - 100);
};

function handleRoundingError<T extends Entry>(dataMap: Map<string, RoundingData>, roundingErrorOffset: number): T {
  const result: Entry = {};
  let lowestValue = 1;
  let lowestValueKey = '';

  // Sets the `lowestValue` to the lowest decimal value. For example. if A=1.935 and B= 1.735,
  // B would have been adjusted more in the rounding than A, if rounding to a whole number. Thusly,
  // We would adjust the rounding error with B, so that values are as close to true as possible.
  dataMap.forEach((value, key) => {
    if (value.truePercent > 0 && value.truePercent % 1 < lowestValue) {
      // sets the lowest value to the remainder value.
      lowestValue = value.truePercent % 1;
      lowestValueKey = key;
    }
  });

  dataMap.forEach((value, key) => {
    if (key === lowestValueKey) {
      result[key] = Number((value.roundedPercent - roundingErrorOffset).toFixed(1));
    } else {
      result[key] = value.roundedPercent;
    }
  });

  return result as T;
}
